import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import Fade from "@mui/material/Fade";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/common/PageHeader";
import TablePagination from "@mui/material/TablePagination";
import ProjectSCurve from "../components/dashboard/ProjectSCurve";

import { getDashboardSummary, getProductivityTrend } from "../api/dashboardApi";
import Charts from "../components/dashboard/Charts";
import * as XLSX from "xlsx";
import { Button } from "@mui/material";

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
} from "@mui/material";

import api from "../api/api";

export default function Dashboard() {
  const { user } = useAuth();

  const [stats, setStats] = useState({
    total_projects: 0,
    open_tasks: 0,
    hours_logged: 0,
  });

  const [trendData, setTrendData] = useState([]);
  const [workItems, setWorkItems] = useState([]);

  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [assigneeFilter, setAssigneeFilter] = useState(user?.name || "");
  const [searchText, setSearchText] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    loadSummary();
    loadTrend();
    loadWorkItems();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, assigneeFilter, projectFilter, clientFilter, searchText]);

  const loadSummary = async () => {
    try {
      const data = await getDashboardSummary();
      setStats(data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadTrend = async () => {
    try {
      const data = await getProductivityTrend();
      setTrendData(Array.isArray(data) ? data : []);
    } catch (error) {
      setTrendData([]);
    }
  };

  const loadWorkItems = async () => {
    try {
      const res = await api.get("/work-items/");
      setWorkItems(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "OPEN":
        return { backgroundColor: "#e0e0e0", fontWeight: 600 };
      case "IN_PROGRESS":
        return { backgroundColor: "#fff176", fontWeight: 600 };
      case "DONE":
        return { backgroundColor: "#a5d6a7", fontWeight: 600 };
      default:
        return {};
    }
  };

  const cards = [
    { label: "Total Projects", value: stats.total_projects },
    { label: "Open Tasks", value: stats.open_tasks },
    { label: "Hours Logged", value: stats.hours_logged },
  ];

  const filteredItems = workItems.filter((item) => {
    const statusMatch =
      statusFilter === "ACTIVE"
        ? item.status === "OPEN" || item.status === "IN_PROGRESS"
        : statusFilter === "ALL"
          ? true
          : item.status === statusFilter;

    const assigneeMatch =
      assigneeFilter === "" || item.assignee_name === assigneeFilter;

    const projectMatch =
      projectFilter === "" || item.project_name === projectFilter;

    const clientMatch =
      clientFilter === "" || item.client_name === clientFilter;

    const searchMatch =
      item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.work_item_code?.toLowerCase().includes(searchText.toLowerCase());

    return (
      statusMatch && assigneeMatch && projectMatch && clientMatch && searchMatch
    );
  });

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const exportToExcel = () => {
    const data = filteredItems.map((item) => ({
      Project: item.project_name,
      Client: item.client_name,
      "Work Item ID": item.work_item_code,
      Task: item.title,
      Status: item.status,
      Assignee: item.assignee_name,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WorkItems");
    XLSX.writeFile(wb, "dashboard_work_items.xlsx");
  };

  return (
    <Fade in timeout={400}>
      <Box className="page" pb={8}>
        <PageHeader title={`Welcome, ${user?.name || ""}`} />

        <Grid container spacing={3}>
          {cards.map((card) => (
            <Grid item xs={12} md={4} key={card.label}>
              <Card
                sx={{
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                }}
              >
                <CardContent>
                  <Typography>{card.label}</Typography>
                  <Typography variant="h4">{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box mt={4} mb={4}>
          <Charts trendData={trendData} />
        </Box>

        <Box mt={4} mb={4}>
          <Card
            sx={{
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent>
              <Typography variant="h6">
                Project Progress (Planned vs Actual)
              </Typography>
              <ProjectSCurve projectId={16} />
            </CardContent>
          </Card>
        </Box>

        <Box mt={4} mb={4}>
          <Card
            sx={{
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent>
              <Typography variant="h6" mb={2}>
                Work Items (All Projects)
              </Typography>

              <Button variant="contained" onClick={exportToExcel}>
                Export Excel
              </Button>

              <Box display="flex" gap={2} mt={2} mb={2} flexWrap="wrap">
                <Select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ACTIVE">Open + In Progress</MenuItem>
                  <MenuItem value="OPEN">OPEN</MenuItem>
                  <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                  <MenuItem value="ALL">All Status</MenuItem>
                </Select>

                <Select
                  size="small"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                >
                  <MenuItem value="">All Assignees</MenuItem>
                  {[...new Set(workItems.map((i) => i.assignee_name))].map(
                    (name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ),
                  )}
                </Select>

                <Select
                  size="small"
                  value={clientFilter}
                  onChange={(e) => {
                    setClientFilter(e.target.value);
                    setProjectFilter(""); // ✅ RESET PROJECT
                  }}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {[...new Set(workItems.map((i) => i.client_name))].map(
                    (name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ),
                  )}
                </Select>

                <Select
                  size="small"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <MenuItem value="">All Projects</MenuItem>

                  {[
                    ...new Set(
                      workItems
                        .filter((i) =>
                          clientFilter ? i.client_name === clientFilter : true,
                        )
                        .map((i) => i.project_name),
                    ),
                  ].map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>

                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search Task / ID"
                />
              </Box>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Work Item ID</TableCell>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assignee</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.project_name}</TableCell>
                      <TableCell>{item.client_name || "-"}</TableCell>
                      <TableCell>{item.work_item_code}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={item.status}
                          sx={getStatusStyle(item.status)}
                          disabled
                        >
                          <MenuItem value="OPEN">OPEN</MenuItem>
                          <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                          <MenuItem value="DONE">DONE</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>{item.assignee_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={filteredItems.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Fade>
  );
}
